import { useMemo, useState } from 'react';
import { Activity, AlertTriangle, ChevronDown, ChevronUp, CheckCircle2, Clock, Cookie, CreditCard, FileText, Loader2, MessageSquare, Package2, RefreshCw, ShieldCheck, TrendingUp, Users, UserCheck, WalletCards, UserX, Youtube } from 'lucide-react';
import { useSubscriptionHealth } from '../hooks/useSubscriptionHealth';
import type { JobState } from '../api/admin';

import AdminShell from '../components/AdminShell';
import HealthReport from '../components/HealthReport';
import PaginationControls from '../components/PaginationControls';
import { useAdminOverview } from '../hooks/useAdminOverview';
import { useConsentStats } from '../hooks/useConsentStats';
import { useYoutubeQuota } from '../hooks/useYoutubeQuota';
import type { ConsentTrendDay, YoutubeQuotaHistoryEntry } from '../api/admin';

const summaryCards = [
  { key: 'totalUsers', label: 'Total users', icon: Users },
  { key: 'blockedUsers', label: 'Blocked users', icon: UserX },
  { key: 'products', label: 'Products', icon: Package2 },
  { key: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
  { key: 'activeSubscriptions', label: 'Active', icon: WalletCards },
] as const;

// ── Consent rate bar ────────────────────────────────────────────────────────
function RateBar({ rate, color }: { rate: number | null; color: string }) {
  if (rate === null) return <p className="text-xs text-zinc-600 italic">No data yet</p>;
  return (
    <div className="mt-1.5">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-zinc-200">{rate}%</span>
        <span className="text-[11px] text-zinc-500">{100 - rate}% declined</span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${rate}%` }} />
      </div>
    </div>
  );
}

// ── 7-day sparkline (bar chart) ──────────────────────────────────────────────
function TrendBars({ trend }: { trend: ConsentTrendDay[] }) {
  const max = Math.max(...trend.map(d => d.accepted + d.rejected), 1);
  return (
    <div className="flex items-end gap-1 h-12">
      {trend.map((d) => {
        const total  = d.accepted + d.rejected;
        const height = Math.round((total / max) * 100);
        const label  = d.date.slice(5); // "MM-DD"
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.date}: ${d.accepted} accepted, ${d.rejected} rejected`}>
            <div className="w-full rounded-t-sm overflow-hidden flex flex-col justify-end" style={{ height: '40px' }}>
              {total === 0 ? (
                <div className="w-full h-px bg-zinc-800" />
              ) : (
                <>
                  <div className="w-full bg-emerald-500/70" style={{ height: `${Math.round((d.accepted / max) * 40)}px` }} />
                  <div className="w-full bg-red-500/40"    style={{ height: `${Math.round((d.rejected / max) * 40)}px` }} />
                </>
              )}
            </div>
            <span className="text-[9px] text-zinc-600">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── YouTube quota gauge ──────────────────────────────────────────────────────
function QuotaBar({ pct, isCritical, isWarning }: { pct: number; isCritical: boolean; isWarning: boolean }) {
  const color = isCritical ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="h-2 rounded-full bg-zinc-800 overflow-hidden mt-2">
      <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

function QuotaSparkline({ history }: { history: YoutubeQuotaHistoryEntry[] }) {
  if (!history.length) return <p className="text-xs text-zinc-600 italic">No history yet</p>;
  const max = Math.max(...history.map(h => h.units), 1);
  return (
    <div className="flex items-end gap-0.5 h-10 mt-2">
      {history.slice(-14).map((h) => {
        const height = Math.round((h.units / max) * 100);
        return (
          <div key={h.date} className="flex-1 flex flex-col items-center justify-end" title={`${h.date}: ${h.units.toLocaleString()} units`}>
            <div className="w-full rounded-t-sm bg-sky-500/60" style={{ height: `${Math.max(2, Math.round(height * 0.38))}px` }} />
          </div>
        );
      })}
    </div>
  );
}

// ── Job card ─────────────────────────────────────────────────────────────────
function JobCard({ name, label, state }: { name: string; label: string; state: JobState }) {
  const hasRun   = Boolean(state.lastRunAt);
  const hasError = Boolean(state.lastError);

  const statusColor = !hasRun
    ? 'text-zinc-500'
    : hasError
      ? 'text-red-400'
      : 'text-emerald-400';

  const statusDot = !hasRun
    ? 'bg-zinc-700'
    : hasError
      ? 'bg-red-500'
      : 'bg-emerald-500';

  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  const durationLabel = (ms: number | null) =>
    ms == null ? '' : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;

  const resultEntries = state.lastResult ? Object.entries(state.lastResult) : [];

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDot}`} />
          <span className="text-xs font-semibold text-zinc-200">{label}</span>
        </div>
        <span className="text-[10px] text-zinc-600 font-mono">{state.scheduleLabel || '—'}</span>
      </div>

      {hasError && (
        <p className="text-[10px] text-red-400 bg-red-900/20 rounded px-2 py-1 break-all">{state.lastError}</p>
      )}

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <span className="text-zinc-500">Last run</span>
        <span className={`text-right font-medium ${statusColor}`}>{hasRun ? fmt(state.lastRunAt) : 'Never'}</span>

        {state.lastDurationMs != null && (
          <>
            <span className="text-zinc-500">Duration</span>
            <span className="text-right text-zinc-400">{durationLabel(state.lastDurationMs)}</span>
          </>
        )}

        <span className="text-zinc-500">Run count</span>
        <span className="text-right text-zinc-400">{state.runCount}</span>

        {resultEntries.map(([k, v]) => (
          <span key={k} className="contents">
            <span className="text-zinc-500 capitalize">{k}</span>
            <span className={`text-right font-semibold ${k === 'failed' && v > 0 ? 'text-red-400' : k === 'expired' || k === 'deleted' || k === 'reset' || k === 'synced' ? 'text-emerald-400' : 'text-zinc-300'}`}>
              {v}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function OverviewPage() {
  const { overview, loading, refresh } = useAdminOverview();
  const { stats: consentStats } = useConsentStats();
  const { stats: quotaStats } = useYoutubeQuota();
  const { data: healthData, loading: healthLoading, refresh: refreshHealth } = useSubscriptionHealth();
  const [productPage, setProductPage] = useState(1);
  const [expandedProductIds, setExpandedProductIds] = useState<Set<number>>(new Set());
  const productPageSize = 6;
  const productPortfolio = useMemo(() => {
    const products = overview?.products || [];
    const total = products.length;
    const totalPages = Math.max(1, Math.ceil(total / productPageSize));
    const currentPage = Math.min(productPage, totalPages);
    const start = (currentPage - 1) * productPageSize;
    return {
      items: products.slice(start, start + productPageSize),
      pagination: {
        total,
        page: currentPage,
        pageSize: productPageSize,
        totalPages,
      },
    };
  }, [overview?.products, productPage]);

  const toggleExpanded = (productId: number) => {
    setExpandedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  return (
    <AdminShell
      title="Overview"
      description="Platform-level summary for Novera Labs products, users, and recurring revenue operations."
      actions={(
        <button
          onClick={() => refresh().catch(() => undefined)}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100 transition-colors text-xs"
        >
          Refresh
        </button>
      )}
    >
      {loading && !overview ? (
        <div className="flex items-center gap-2 text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading overview…
        </div>
      ) : null}

      {overview ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            {summaryCards.map(({ key, label, icon: Icon }) => (
              <div key={key} className="rounded-2xl border border-zinc-800 bg-zinc-900 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">{label}</span>
                  <Icon className="w-4 h-4 text-amber-500" />
                </div>
                <p className="mt-3 text-2xl font-semibold text-zinc-100">{overview.summary[key]}</p>
              </div>
            ))}
          </div>

          <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
            <h2 className="text-lg font-semibold text-zinc-100">Product Portfolio</h2>
            <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
              {productPortfolio.items.map((product) => {
                const isExpanded = expandedProductIds.has(product.id);
                return (
                  <div key={product.id} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-zinc-100">{product.name}</p>
                        <p className="text-xs text-zinc-500 mt-1">{product.code}</p>
                        {product.description ? <p className="text-xs text-zinc-400 mt-2">{product.description}</p> : null}
                      </div>
                      <span className="px-2 py-1 rounded-full border border-zinc-700 bg-zinc-900 text-xs text-zinc-300">{product.status}</span>
                    </div>
                    {/* Subscription counts */}
                  <div className="mt-4 grid grid-cols-4 gap-3 text-xs">
                      <div className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2">
                        <p className="text-zinc-500">Plans</p>
                        <p className="mt-1 text-zinc-100 font-semibold">{product.planCount}</p>
                      </div>
                      <div className="rounded-lg border border-emerald-900/30 bg-emerald-950/10 px-3 py-2">
                        <p className="text-emerald-500/80">Active</p>
                        <p className="mt-1 text-emerald-400 font-semibold">{product.activeSubscriptionCount}</p>
                      </div>
                      <div className="rounded-lg border border-amber-900/30 bg-amber-950/10 px-3 py-2">
                        <p className="text-amber-500/80">Cancelled</p>
                        <p className="mt-1 text-amber-400 font-semibold">{product.cancelledSubscriptionCount}</p>
                      </div>
                      <div className="rounded-lg border border-red-900/30 bg-red-950/10 px-3 py-2">
                        <p className="text-red-500/80">Revoked</p>
                        <p className="mt-1 text-red-400 font-semibold">{product.revokedSubscriptionCount}</p>
                      </div>
                    </div>

                  {/* Product Insights */}
                  {product.insights && (
                    <div className="mt-4">
                      <p className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wider mb-2">Product Insights</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-2.5 rounded-lg border border-zinc-800 bg-zinc-900/80 px-3 py-2.5">
                          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-500/10 flex-shrink-0">
                            <UserCheck className="w-3.5 h-3.5 text-sky-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-zinc-500 truncate">Active Creators</p>
                            <p className="text-sky-300 font-bold text-sm leading-tight">
                              {product.insights.activeCreators.toLocaleString('en-IN')}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2.5 rounded-lg border border-zinc-800 bg-zinc-900/80 px-3 py-2.5">
                          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-500/10 flex-shrink-0">
                            <MessageSquare className="w-3.5 h-3.5 text-violet-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-zinc-500 truncate">Comments Analysed</p>
                            <p className="text-violet-300 font-bold text-sm leading-tight">
                              {product.insights.totalCommentsAnalysed.toLocaleString('en-IN')}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2.5 rounded-lg border border-zinc-800 bg-zinc-900/80 px-3 py-2.5">
                          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-500/10 flex-shrink-0">
                            <Clock className="w-3.5 h-3.5 text-amber-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-zinc-500 truncate">Hours Saved</p>
                            <p className="text-amber-300 font-bold text-sm leading-tight">
                              {product.insights.hoursSaved.toLocaleString('en-IN')}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2.5 rounded-lg border border-zinc-800 bg-zinc-900/80 px-3 py-2.5">
                          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/10 flex-shrink-0">
                            <FileText className="w-3.5 h-3.5 text-emerald-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-zinc-500 truncate">Content Ideas</p>
                            <p className="text-emerald-300 font-bold text-sm leading-tight">
                              {product.insights.contentIdeasGenerated.toLocaleString('en-IN')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                    {(product.plans?.length || 0) > 0 ? (
                      <div className="mt-3">
                        <button
                          onClick={() => toggleExpanded(product.id)}
                          className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-200 transition-colors"
                        >
                          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {isExpanded ? 'Hide' : 'View'} Plan Details
                        </button>

                        {isExpanded ? (
                          <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-zinc-500 border-b border-zinc-800">
                                  <th className="py-2 px-3">Plan</th>
                                  <th className="py-2 px-3 text-center text-emerald-500/80">Active</th>
                                  <th className="py-2 px-3 text-center text-amber-500/80">Cancelled</th>
                                  <th className="py-2 px-3 text-center text-red-500/80">Revoked</th>
                                </tr>
                              </thead>
                              <tbody>
                                {product.plans.map((plan) => (
                                  <tr key={plan.id} className="border-b border-zinc-800/50 last:border-0">
                                    <td className="py-2 px-3 text-zinc-200">{plan.name}</td>
                                    <td className="py-2 px-3 text-center text-emerald-400 font-semibold">{plan.activeCount}</td>
                                    <td className="py-2 px-3 text-center text-amber-400 font-semibold">{plan.cancelledCount}</td>
                                    <td className="py-2 px-3 text-center text-red-400 font-semibold">{plan.revokedCount}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <PaginationControls pagination={productPortfolio.pagination} onPageChange={setProductPage} />
          </section>

          {/* ── YouTube API Quota ─────────────────────────────────────────── */}
          <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Youtube className="w-4 h-4 text-red-500" />
              <h2 className="text-lg font-semibold text-zinc-100">YouTube API Quota</h2>
              {quotaStats?.isCritical && (
                <span className="ml-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-950/50 border border-red-800 text-xs text-red-400">
                  <AlertTriangle className="w-3 h-3" /> Critical
                </span>
              )}
              {!quotaStats?.isCritical && quotaStats?.isWarning && (
                <span className="ml-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-950/50 border border-amber-800 text-xs text-amber-400">
                  <AlertTriangle className="w-3 h-3" /> Warning
                </span>
              )}
              {quotaStats && !quotaStats.isCritical && !quotaStats.isWarning && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-950/50 border border-emerald-900 text-xs text-emerald-400">Healthy</span>
              )}
              <span className="ml-auto text-xs text-zinc-500">{quotaStats?.date ?? '—'}</span>
            </div>

            {!quotaStats ? (
              <p className="text-sm text-zinc-600">Loading quota data…</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* ── Usage numbers ── */}
                <div className="flex flex-col gap-3">
                  <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-zinc-400">Units used today</span>
                      <Activity className="w-3.5 h-3.5 text-zinc-500" />
                    </div>
                    <p className={`text-2xl font-semibold ${quotaStats.isCritical ? 'text-red-400' : quotaStats.isWarning ? 'text-amber-400' : 'text-zinc-100'}`}>
                      {quotaStats.unitsUsed.toLocaleString()}
                    </p>
                    <p className="text-[11px] text-zinc-600 mt-0.5">of {quotaStats.dailyLimit.toLocaleString()} daily limit</p>
                    <QuotaBar pct={quotaStats.percentUsed} isCritical={quotaStats.isCritical} isWarning={quotaStats.isWarning} />
                  </div>

                  <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-zinc-400">Remaining</span>
                      <span className="text-[11px] text-zinc-500">{quotaStats.percentUsed}% used</span>
                    </div>
                    <p className="text-2xl font-semibold text-zinc-100">{quotaStats.remaining.toLocaleString()}</p>
                    <p className="text-[11px] text-zinc-600 mt-0.5">
                      Resets at {new Date(quotaStats.nextReset).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} UTC
                    </p>
                  </div>
                </div>

                {/* ── 14-day sparkline ── */}
                <div className="lg:col-span-2 rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 flex flex-col justify-between">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">14-Day Usage History</p>
                    <span className="text-[11px] text-zinc-500">hover bars for detail</span>
                  </div>
                  <QuotaSparkline history={quotaStats.history} />
                  <p className="text-[11px] text-zinc-600 mt-3">
                    Each bar = total YouTube API units consumed on that quota day (resets 08:00 UTC). Restarts to 0 on server restart.
                  </p>
                </div>
              </div>
            )}
          </section>

          {/* ── Cookie Consent Insights ───────────────────────────────────── */}
          <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Cookie className="w-4 h-4 text-amber-500" />
              <h2 className="text-lg font-semibold text-zinc-100">Cookie Consent</h2>
              <span className="ml-auto text-xs text-zinc-500">Last 7 days + all-time · auto-refreshes every 30s</span>
            </div>

            {!consentStats ? (
              <p className="text-sm text-zinc-600">Loading consent data…</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* ── Stat cards ── */}
                <div className="flex flex-col gap-3">
                  <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-zinc-400">Total responses</span>
                      <Users className="w-3.5 h-3.5 text-zinc-500" />
                    </div>
                    <p className="text-2xl font-semibold text-zinc-100">{consentStats.total.toLocaleString()}</p>
                    {consentStats.legacyTotal > 0 && (
                      <p className="text-[11px] text-zinc-600 mt-1">
                        {consentStats.legacyTotal} legacy · {consentStats.granularTotal} granular
                      </p>
                    )}
                  </div>

                  <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-zinc-400 flex items-center gap-1.5">
                        <ShieldCheck className="w-3 h-3 text-emerald-500" /> Analytics opt-in
                      </span>
                      <span className="text-[11px] text-zinc-500">{consentStats.analyticsAccepted} banner responses</span>
                    </div>
                    <RateBar rate={consentStats.analyticsRate} color="bg-emerald-500" />
                  </div>

                  <div className="rounded-xl border border-sky-900/40 bg-sky-950/20 p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-zinc-400 flex items-center gap-1.5">
                        <ShieldCheck className="w-3 h-3 text-sky-400" /> User preferences
                        <span className="text-[10px] text-zinc-600">(profile toggle)</span>
                      </span>
                      <span className="text-[11px] text-zinc-500">{consentStats.userTotal} accounts</span>
                    </div>
                    <RateBar rate={consentStats.userOptInRate} color="bg-sky-500" />
                    <div className="mt-2 flex justify-between text-[11px] text-zinc-500">
                      <span className="text-emerald-400">{consentStats.userOptIn} opted in</span>
                      <span className="text-red-400">{consentStats.userExplicitOptOut} opted out</span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-zinc-400 flex items-center gap-1.5">
                        <TrendingUp className="w-3 h-3 text-violet-400" /> Marketing opt-in
                      </span>
                      <span className="text-[11px] text-zinc-500">{consentStats.marketingAccepted} users</span>
                    </div>
                    <RateBar rate={consentStats.marketingRate} color="bg-violet-500" />
                  </div>

                  {consentStats.legacyTotal > 0 && (
                    <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-zinc-400">Legacy accept rate</span>
                        <span className="text-[11px] text-zinc-500">{consentStats.legacyAccepted} / {consentStats.legacyTotal}</span>
                      </div>
                      <RateBar rate={consentStats.legacyRate} color="bg-amber-500" />
                    </div>
                  )}
                </div>

                {/* ── 7-day trend ── */}
                <div className="lg:col-span-2 rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 flex flex-col justify-between">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">7-Day Trend</p>
                    <div className="flex items-center gap-3 text-[11px] text-zinc-500">
                      <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-emerald-500/70" />Accepted</span>
                      <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-red-500/40" />Declined</span>
                    </div>
                  </div>
                  <TrendBars trend={consentStats.trend} />
                  <p className="text-[11px] text-zinc-600 mt-3">
                    Each bar = total consent responses for that day (green = analytics accepted, red = declined).
                  </p>
                </div>
              </div>
            )}
          </section>

          {/* ── Subscription Job Health ────────────────────────────────────── */}
          <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
            <div className="flex items-center gap-2 mb-5">
              <RefreshCw className="w-4 h-4 text-violet-400" />
              <h2 className="text-lg font-semibold text-zinc-100">Subscription Jobs</h2>
              <span className="ml-auto flex items-center gap-2">
                {healthData && (
                  <span className="text-[10px] text-zinc-600 flex items-center gap-2">
                    <span>as of {new Date(healthData.generatedAt).toLocaleTimeString('en-IN')}</span>
                    <span className="text-zinc-700">·</span>
                    <span>server up since {new Date(healthData.serverStartedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </span>
                )}
                <button
                  onClick={refreshHealth}
                  disabled={healthLoading}
                  className="flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-800 px-2.5 py-1 text-[11px] text-zinc-300 hover:bg-zinc-700 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3 h-3 ${healthLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </span>
            </div>

            {healthLoading && !healthData ? (
              <div className="flex items-center justify-center py-12 text-zinc-600 gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : healthData ? (
              <div className="space-y-6">

                {/* Alert bar */}
                {(healthData.subscriptions.ghostUsers > 0 ||
                  healthData.subscriptions.staleCheckouts > 0 ||
                  healthData.subscriptions.expiringSoon24h > 0) && (
                  <div className="rounded-xl border border-amber-800/50 bg-amber-950/30 px-4 py-3 flex flex-wrap gap-4 text-xs">
                    <span className="font-semibold text-amber-400 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> Needs attention
                    </span>
                    {healthData.subscriptions.ghostUsers > 0 && (
                      <span className="text-amber-300">{healthData.subscriptions.ghostUsers} ghost user{healthData.subscriptions.ghostUsers > 1 ? 's' : ''} (plan stuck at basic)</span>
                    )}
                    {healthData.subscriptions.staleCheckouts > 0 && (
                      <span className="text-amber-300">{healthData.subscriptions.staleCheckouts} stale checkout{healthData.subscriptions.staleCheckouts > 1 ? 's' : ''} ({'>'}24 h old)</span>
                    )}
                    {healthData.subscriptions.expiringSoon24h > 0 && (
                      <span className="text-amber-300">{healthData.subscriptions.expiringSoon24h} grace-period sub{healthData.subscriptions.expiringSoon24h > 1 ? 's' : ''} expiring within 24 h</span>
                    )}
                  </div>
                )}

                {/* Job cards */}
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">Background jobs</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                    {(Object.entries(healthData.jobs) as [string, JobState][]).map(([key, state]) => {
                      const labels: Record<string, string> = {
                        expireEndedSubscriptions: 'Expire ended subs',
                        purgeStaleCheckouts:      'Purge stale checkouts',
                        syncActiveSubscriptions:  'Sync from Razorpay',
                        fixGhostedUsers:          'Fix ghost users',
                      };
                      return <JobCard key={key} name={key} label={labels[key] || key} state={state} />;
                    })}
                  </div>
                </div>

                {/* Subscription counts */}
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">Subscription state</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[
                      { label: 'Active',        key: 'active',        color: 'text-emerald-400' },
                      { label: 'Authenticated', key: 'authenticated',  color: 'text-sky-400'     },
                      { label: 'Cancelled',     key: 'cancelled',      color: 'text-amber-400'   },
                      { label: 'Halted',        key: 'halted',         color: 'text-orange-400'  },
                      { label: 'Completed',     key: 'completed',      color: 'text-zinc-400'    },
                      { label: 'Pending',       key: 'created',        color: 'text-zinc-500'    },
                    ].map(({ label, key, color }) => (
                      <div key={key} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 text-center">
                        <p className={`text-2xl font-black tabular-nums ${color}`}>
                          {(healthData.subscriptions.byStatus[key] || 0).toLocaleString()}
                        </p>
                        <p className="text-[10px] text-zinc-500 mt-0.5">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Watchlist row */}
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">Watchlist</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Ghost users',         value: healthData.subscriptions.ghostUsers,         warn: true  },
                      { label: 'Stale checkouts',     value: healthData.subscriptions.staleCheckouts,     warn: true  },
                      { label: 'Expiring ≤ 24 h',     value: healthData.subscriptions.expiringSoon24h,    warn: true  },
                      { label: 'Expired last 24 h',   value: healthData.subscriptions.recentlyCompleted24h, warn: false },
                    ].map(({ label, value, warn }) => (
                      <div key={label} className={`rounded-xl border p-3 text-center ${warn && value > 0 ? 'border-amber-800/50 bg-amber-950/20' : 'border-zinc-800 bg-zinc-950/60'}`}>
                        <p className={`text-2xl font-black tabular-nums ${warn && value > 0 ? 'text-amber-400' : 'text-zinc-300'}`}>
                          {value.toLocaleString()}
                        </p>
                        <p className={`text-[10px] mt-0.5 ${warn && value > 0 ? 'text-amber-600' : 'text-zinc-500'}`}>{label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Per-plan breakdown */}
                {healthData.plans.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">Plan breakdown</p>
                    <div className="overflow-x-auto rounded-xl border border-zinc-800">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-zinc-800">
                            <th className="text-left px-4 py-2.5 text-zinc-500 font-medium">Plan</th>
                            <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Interval</th>
                            <th className="text-right px-3 py-2.5 text-emerald-500 font-medium">Active</th>
                            <th className="text-right px-3 py-2.5 text-sky-500 font-medium">Auth'd</th>
                            <th className="text-right px-3 py-2.5 text-amber-500 font-medium">Cancelled</th>
                            <th className="text-right px-3 py-2.5 text-orange-500 font-medium">Halted</th>
                          </tr>
                        </thead>
                        <tbody>
                          {healthData.plans.map((p) => (
                            <tr key={p.code} className="border-b border-zinc-800/50 last:border-0 hover:bg-zinc-800/30">
                              <td className="px-4 py-2.5 text-zinc-200 font-medium">{p.name}</td>
                              <td className="px-3 py-2.5 text-zinc-500 capitalize">{p.intervalUnit}</td>
                              <td className="px-3 py-2.5 text-right text-emerald-400 font-semibold tabular-nums">{p.active}</td>
                              <td className="px-3 py-2.5 text-right text-sky-400 tabular-nums">{p.authenticated}</td>
                              <td className="px-3 py-2.5 text-right text-amber-400 tabular-nums">{p.cancelled}</td>
                              <td className="px-3 py-2.5 text-right text-orange-400 tabular-nums">{p.halted}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              </div>
            ) : (
              <p className="text-sm text-zinc-600 text-center py-8">Failed to load subscription health data.</p>
            )}
          </section>

          {/* ── System Health ─────────────────────────────────────────────── */}
          <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
            <div className="flex items-center gap-2 mb-5">
              <Activity className="w-4 h-4 text-sky-400" />
              <h2 className="text-lg font-semibold text-zinc-100">System Health</h2>
              <span className="ml-auto text-xs text-zinc-500">On-demand — click to collect</span>
            </div>
            <HealthReport />
          </section>
        </>
      ) : null}
    </AdminShell>
  );
}
